// Keeps a scene's mandatory rule consistent with the content it depends on.
// A mandatoryType is "built on" a specific kind of element on the scene
// (sound / video / one-click / question / animation). When that element is
// deleted the rule has nothing left to gate on, so callers reset the scene's
// mandatory (see reconcileMandatoryForScene in actions). The checks below
// mirror ViewToolbar's has*InScene helpers but read from a plain `present`
// state slice so they can run inside thunks/reducers.

const VIDEO_PLUGINS = ["EnrichedPlayer", "VideoCard"];
const ONE_CLICK_PLUGIN = "OneClick";
export const QUESTION_PLUGINS = [
  "MultipleAnswer",
  "MultipleChoice",
  "FreeResponse",
  "DragDrop",
  "FillInBlank",
  "InputText",
  "Matching",
  "TrueFalse",
  "Ordering",
];

export const MANDATORY_RESET = {
  isMandatory: false,
  mandatoryType: "waitForTime",
};

function sceneNavItem(present, sceneId) {
  return (
    (present.navItemsById && present.navItemsById[sceneId]) ||
    (present.containedViewsById && present.containedViewsById[sceneId]) ||
    null
  );
}

export function sceneHasSounds(present, sceneId) {
  const sounds = present.soundList && present.soundList[sceneId];
  if (!sounds) return false;
  return Array.isArray(sounds) ? sounds.length > 0 : Object.keys(sounds).length > 0;
}

function sceneBoxesHavePlugin(present, sceneId, pluginIds) {
  const nav = sceneNavItem(present, sceneId);
  const plugins = present.pluginToolbarsById;
  if (!nav || !nav.boxes || !plugins) return false;
  return nav.boxes.some((boxId) => {
    const plugin = plugins[boxId];
    return plugin && pluginIds.indexOf(plugin.pluginId) !== -1;
  });
}

export function sceneHasVideos(present, sceneId) {
  return sceneBoxesHavePlugin(present, sceneId, VIDEO_PLUGINS);
}

export function sceneHasOneClick(present, sceneId) {
  return sceneBoxesHavePlugin(present, sceneId, [ONE_CLICK_PLUGIN]);
}

export function sceneHasQuestions(present, sceneId) {
  if (sceneBoxesHavePlugin(present, sceneId, QUESTION_PLUGINS)) return true;
  const ex =
    present.exercises && present.exercises[sceneId] && present.exercises[sceneId].exercises;
  return !!(ex && Object.keys(ex).length > 0);
}

export function sceneHasAnimations(present, sceneId) {
  const nav = sceneNavItem(present, sceneId);
  if (!nav || !nav.boxes || nav.boxes.length === 0) return false;
  const anims = present.animationsById || {};
  const boxes = present.boxesById || {};
  return nav.boxes.some((boxId) => {
    if (anims[boxId] && Object.keys(anims[boxId]).length > 0) return true;
    const box = boxes[boxId];
    return !!(
      box &&
      ((box.boxAnimation && box.boxAnimation.animation) ||
        (box.textAnimation && box.textAnimation.animation))
    );
  });
}

// True when the scene is mandatory on a media/interaction type whose backing
// element no longer exists on the scene. waitForTime is always satisfiable so
// it never needs resetting.
export function mandatoryDependencyMissing(present, sceneId) {
  const tb = present && present.viewToolbarsById && present.viewToolbarsById[sceneId];
  if (!tb || !tb.isMandatory) return false;
  switch (tb.mandatoryType) {
    case "waitForOneClick":
      return !sceneHasOneClick(present, sceneId);
    // TODO: deprecated — waitForSound/waitForVideo/waitForAnimation can no
    // longer be authored; these cases (and their sceneHas* helpers) only serve
    // legacy trainings. Remove once no trainings still use them.
    case "waitForSound":
      return !sceneHasSounds(present, sceneId);
    case "waitForVideo":
      return !sceneHasVideos(present, sceneId);
    case "waitForAnimation":
      return !sceneHasAnimations(present, sceneId);
    case "waitForAction":
    case "waitForCorrectAction":
      return !sceneHasQuestions(present, sceneId);
    default:
      return false;
  }
}
